import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { sanitarywareService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";

const SanitarywareDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await sanitarywareService.getById(id);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await sanitarywareService.updateStatus(id, { status: newStatus });
      setProduct((prev) => ({ ...prev, status: newStatus }));
      toast.success("Status updated successfully");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handlePublishToggle = async () => {
    try {
      const newPublished = !product.published;
      await sanitarywareService.updatePublishStatus(id, {
        published: newPublished,
      });
      setProduct((prev) => ({ ...prev, published: newPublished }));
      toast.success(
        `Product ${newPublished ? "published" : "unpublished"} successfully`
      );
    } catch (error) {
      console.error("Error updating publish status:", error);
      toast.error("Failed to update publish status");
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <h4>Product not found</h4>
                <Link to="/products/sanitaryware" className="btn btn-primary">
                  Back to Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Product Details</h4>
            <div className="card-actions">
              <Link
                to={`/products/sanitaryware/edit/${id}`}
                className="btn btn-primary"
              >
                Edit Product
              </Link>
              <Link
                to="/products/sanitaryware"
                className="btn btn-secondary ml-2"
              >
                Back to List
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                {/* Product Images */}
                <div className="product-images">
                  {product.images && product.images.length > 0 ? (
                    <>
                      <div className="main-image mb-3">
                        <img
                          src={product.images[activeImage]}
                          alt={product.name}
                          className="img-fluid rounded"
                          style={{
                            width: "100%",
                            height: "400px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      {product.images.length > 1 && (
                        <div className="thumbnail-images">
                          <div className="row">
                            {product.images.map((image, index) => (
                              <div key={index} className="col-3 mb-2">
                                <img
                                  src={image}
                                  alt={`${product.name} ${index + 1}`}
                                  className={`img-thumbnail cursor-pointer ${
                                    activeImage === index
                                      ? "border-primary"
                                      : ""
                                  }`}
                                  style={{
                                    width: "100%",
                                    height: "80px",
                                    objectFit: "cover",
                                  }}
                                  onClick={() => setActiveImage(index)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-image-placeholder text-center">
                      <div
                        className="bg-light rounded d-flex align-items-center justify-content-center"
                        style={{ height: "400px" }}
                      >
                        <i className="fas fa-image fa-4x text-muted"></i>
                      </div>
                      <p className="text-muted mt-2">No images available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                {/* Product Information */}
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <div className="mb-3">
                    <span className="h4 text-primary">
                      ₹{product.price?.toLocaleString()}
                    </span>
                  </div>

                  <div className="mb-3">
                    <span
                      className={`badge badge-${
                        product.status === "active" ? "success" : "danger"
                      } mr-2`}
                    >
                      {product.status}
                    </span>
                    <span
                      className={`badge badge-${
                        product.published ? "info" : "warning"
                      }`}
                    >
                      {product.published ? "Published" : "Draft"}
                    </span>
                  </div>

                  <div className="mb-3">
                    <h6>Description:</h6>
                    <p>{product.description || "No description available"}</p>
                  </div>

                  <div className="mb-3">
                    <h6>Product Type:</h6>
                    <p>{product.type?.name || "N/A"}</p>
                  </div>

                  {/* Specifications */}
                  {product.specifications &&
                    Object.keys(product.specifications).length > 0 && (
                      <div className="mb-3">
                        <h6>Specifications:</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <tbody>
                              {Object.entries(product.specifications).map(
                                ([key, value]) => (
                                  <tr key={key}>
                                    <td>
                                      <strong>{key}:</strong>
                                    </td>
                                    <td>{value}</td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                  {/* Actions */}
                  <div className="mb-3">
                    <h6>Actions:</h6>
                    <div className="btn-group" role="group">
                      <button
                        className={`btn btn-sm ${
                          product.status === "active"
                            ? "btn-warning"
                            : "btn-success"
                        }`}
                        onClick={() =>
                          handleStatusChange(
                            product.status === "active" ? "inactive" : "active"
                          )
                        }
                      >
                        {product.status === "active"
                          ? "Deactivate"
                          : "Activate"}
                      </button>
                      <button
                        className={`btn btn-sm ${
                          product.published ? "btn-warning" : "btn-info"
                        }`}
                        onClick={handlePublishToggle}
                      >
                        {product.published ? "Unpublish" : "Publish"}
                      </button>
                      <Link
                        to={`/products/sanitaryware/images/${id}`}
                        className="btn btn-sm btn-secondary"
                      >
                        Manage Images
                      </Link>
                    </div>
                  </div>

                  {/* Meta Information */}
                  <div className="meta-info">
                    <small className="text-muted">
                      <strong>Created:</strong>{" "}
                      {product.created_at
                        ? new Date(product.created_at).toLocaleDateString()
                        : "N/A"}
                      <br />
                      <strong>Updated:</strong>{" "}
                      {product.updated_at
                        ? new Date(product.updated_at).toLocaleDateString()
                        : "N/A"}
                      <br />
                      <strong>Product ID:</strong> {product.id}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanitarywareDetail;
